import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { CircularProgress } from '@/UI/CircularProgress/CircularProgress';
import { FlexCenHorVer, FlexColCenHorVer } from '@/UI/styles';
import { TextIcon } from '@/UI/Icons/TextIcon';
import { SpeakerHighIcon } from '@/UI/Icons/SpeakerHighIcon';
import { ThreeDIcon } from '@/UI/Icons/ThreeDIcon';
import { ImageIcon } from '@/UI/Icons/ImageIcon';
import { useResolvedAssets } from '@/state/workflow.state';
import { thumbnailSource } from '@/utils/assetUrls';
import { AssetThumbnail } from '@/components/Common/AssetThumbnail';
import type { ThumbnailSource } from '@/utils/assetUrls';
import type { RenderingAsset, MediaAsset, AssetType, TextAsset } from '@/types/api/assets';

type NoPreviewType = Extract<AssetType, 'text' | 'audio' | '3D'>;
type PlaceholderThumbnailType = NoPreviewType | 'image' | 'video';

const StyledMedia = ({
  selected,
  index,
  source,
  alt,
  borderRadius,
}: {
  selected: number;
  index: number;
  source: ThumbnailSource;
  alt: string;
  borderRadius: number;
}) => {
  return (
    <AssetThumbnail
      source={source}
      alt={alt}
      draggable={false}
      sx={{
        display: 'block',
        height: '100%',
        width: '100%',
        objectFit: 'cover',
        filter: selected !== index ? 'grayscale(100%) brightness(60%)' : 'none',
        cursor: 'pointer',
        borderRadius,
        '&:hover': {
          filter: selected !== index ? 'grayscale(100%) brightness(80%)' : 'none',
        },
      }}
    />
  );
};

const ThumbnailIcon: Record<NoPreviewType, React.ReactNode> = {
  text: <TextIcon width={32} height={32} />,
  audio: <SpeakerHighIcon width={32} height={32} />,
  '3D': <ThreeDIcon width={32} height={32} />,
};

const Thumbnail = ({ isSelected, type }: { isSelected: boolean; type: PlaceholderThumbnailType }) => {
  return (
    <FlexCenHorVer
      sx={{
        bgcolor: isSelected ? token.bgSelected : token.bgSecondary,
        border: `1px solid ${isSelected ? token.borderSelectedStrong : token.border}`,
        '&:hover': {
          bgcolor: token.bgHover,
        },
        height: '100%',
        width: '100%',
        cursor: 'pointer',
        borderRadius: 1,
      }}
    >
      {ThumbnailIcon[type] || <ImageIcon width={32} height={32} />}
    </FlexCenHorVer>
  );
};

interface GalleryThumbnailViewerProps {
  file: MediaAsset | TextAsset | RenderingAsset;
  globalIndex: number;
  selected: number;
}

export const GalleryThumbnailViewer = ({ file, globalIndex, selected }: GalleryThumbnailViewerProps) => {
  const resolvedAssets = useResolvedAssets();

  if (file.type === 'image' || file.type === 'video') {
    const source = thumbnailSource(file, resolvedAssets);

    if (source.kind === 'none') {
      return <Thumbnail isSelected={selected === globalIndex} type={file.type} />;
    }

    return (
      <StyledMedia
        source={source}
        alt={`Image ${globalIndex}`}
        selected={selected}
        index={globalIndex}
        borderRadius={1}
      />
    );
  }

  if (file.type === 'audio') {
    return <Thumbnail isSelected={selected === globalIndex} type="audio" />;
  }

  if (file.type === 'rendering') {
    return (
      <FlexColCenHorVer
        sx={{
          height: '100%',
          borderRadius: 1,
          border: `1px solid ${token.border}`,
          backgroundColor: token.bgSecondary,
        }}
      >
        <CircularProgress size={20} sx={{ color: token.textSecondary }} />
      </FlexColCenHorVer>
    );
  }

  return <Thumbnail isSelected={selected === globalIndex} type={file.type} />;
};
